import { useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useLingui } from "@lingui/react/macro";
import { Button } from "@/components/ui/button";
import { neighbourTab, sessionTone } from "@/lib/multi-view";
import type { BatchScale } from "@/lib/recipes/staged-method";
import type { CookingSession } from "@/types/cooking-session";
import { RecipeMethodPanel, type MethodLayout } from "./RecipeMethodPanel";
import { SegmentedControl } from "./SegmentedControl";
import { SessionChip } from "./SessionChip";
import { SessionTopBar } from "./SessionTopBar";

interface TabsViewProps {
  sessions: CookingSession[];
  activeId: number | null;
  onActiveChange: (id: number) => void;
  secondsOf: (session: CookingSession) => number;
  scaleOf: (id: number) => BatchScale;
  onScaleChange: (id: number, scale: BatchScale) => void;
  onError: (message: string) => void;
}

/**
 * Tabs view (YC-24): one recipe at a time, full size. The chips are the tabs
 * and keep their timers running; the arrows step through them.
 */
export function TabsView({ sessions, activeId, onActiveChange, secondsOf, scaleOf, onScaleChange, onError }: TabsViewProps) {
  const { t } = useLingui();
  const [layout, setLayout] = useState<MethodLayout>("staged");
  const ids = sessions.map((s) => s.id);
  const active = sessions.find((s) => s.id === activeId) ?? sessions[0];
  if (!active) return null;
  const index = ids.indexOf(active.id);
  const go = (step: -1 | 1) => {
    const target = neighbourTab(ids, active.id, step);
    if (target !== null) onActiveChange(target);
  };

  return (
    <div className="flex flex-col gap-4" data-testid="tabs-view">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div role="tablist" aria-label={t`Recipes`} className="flex flex-wrap items-center gap-2">
          {sessions.map((session) => (
            <SessionChip
              key={session.id}
              name={session.recipeName}
              tone={sessionTone(session, secondsOf(session))}
              seconds={secondsOf(session)}
              active={session.id === active.id}
              onSelect={() => onActiveChange(session.id)}
            />
          ))}
        </div>
        <div className="flex items-center gap-2">
          <Button type="button" variant="brand-outline" size="icon" disabled={index <= 0} onClick={() => go(-1)} aria-label={t`Previous recipe`}>
            <ChevronLeft />
          </Button>
          <span className="min-w-14 text-center text-sm font-medium tabular-nums text-(--color-text-secondary)" data-testid="tab-position">
            {t`${index + 1} of ${sessions.length}`}
          </span>
          <Button type="button" variant="brand-outline" size="icon" disabled={index >= sessions.length - 1} onClick={() => go(1)} aria-label={t`Next recipe`}>
            <ChevronRight />
          </Button>
        </div>
      </div>

      <SessionTopBar session={active} seconds={secondsOf(active)} onError={onError} />

      <div className="flex flex-col gap-4" role="tabpanel" aria-label={active.recipeName}>
        <div className="flex justify-end">
          <SegmentedControl<MethodLayout>
            aria-label={t`Recipe layout`}
            value={layout}
            onChange={setLayout}
            options={[
              { value: "staged", label: t`Staged` },
              { value: "simple", label: t`Simple` },
            ]}
          />
        </div>
        <RecipeMethodPanel
          recipeId={active.recipeId}
          layout={layout}
          scale={scaleOf(active.id)}
          onScaleChange={(scale) => onScaleChange(active.id, scale)}
        />
      </div>
    </div>
  );
}
